import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown, Search } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { useAvailableHelpFeatures } from '@/hooks/useAvailableHelpFeatures';
import { HELP_CATEGORIES, EXTRA_SHORTCUTS, type HelpFeature } from '@/lib/helpContent';
import { LATEST_RELEASE } from '@/lib/whatsNew';

/** Dispatch on window to open the help panel from anywhere */
export const OPEN_HELP_EVENT = 'open-help-panel';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const keyLabel = (k: string) => (k === 'mod' ? (isMac ? '⌘' : 'Ctrl') : k);

export function Keys({ keys }: { keys: string[] }) {
  return (
    <span className="inline-flex items-center gap-1">
      {keys.map(k => (
        <kbd key={k} className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-muted px-1.5 font-mono text-2xs font-medium text-muted-foreground">
          {keyLabel(k)}
        </kbd>
      ))}
    </span>
  );
}

const onPage = (f: HelpFeature, path: string) =>
  f.pages.some(p => p === '*' || (p === '/' ? path === '/' : path.startsWith(p)));

/** Latest release notes. `isNew` = first time this user sees them since the update. */
function WhatsNew({ isNew }: { isNew: boolean }) {
  // Open by itself only right after an update; otherwise one line the user can expand
  const [expanded, setExpanded] = useState(isNew);
  if (!LATEST_RELEASE) return null;
  return (
    <section>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-1 text-left text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        What's new in {LATEST_RELEASE.version}
        {isNew && <span className="rounded-full bg-primary/10 px-1.5 py-px text-2xs font-medium text-primary">New</span>}
        <ChevronDown className={`ml-auto w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden />
      </button>
      {expanded && <ul className="mt-1.5 space-y-1.5 px-3">
        {LATEST_RELEASE.items.map(item => (
          <li key={item.title + item.text} className="text-sm text-muted-foreground">
            {item.title && <span className="text-foreground">{item.title}. </span>}
            {item.text}
          </li>
        ))}
      </ul>}
    </section>
  );
}

export default function HelpPanel({ open, onOpenChange, whatsNewFirst = false }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Just updated: show the release notes above everything else, once */
  whatsNewFirst?: boolean;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [query, setQuery] = useState('');

  useEffect(() => { if (!open) setQuery(''); }, [open]);

  // Only show what this user can actually use on their plan
  const available = useAvailableHelpFeatures();

  const q = query.trim().toLowerCase();
  const matches = q
    ? available.filter(f => [f.title, f.benefit, f.how, f.category].some(t => t.toLowerCase().includes(q)))
    : [];
  // "Everywhere" features (search, new patient) live in Everyday, not "On this page"
  const here = available.filter(f => !f.pages.includes('*') && onPage(f, pathname));
  const rest = available.filter(f => !here.includes(f));

  const tryIt = (f: HelpFeature) => {
    if (!f.to) return;
    if (f.newWindow) window.open(f.to, '_blank');
    else { navigate(f.to); onOpenChange(false); }
  };


  const renderFeature = (f: HelpFeature) => {
    const canOpen = !!f.to && !(f.to === pathname && !f.newWindow);
    const body = (
      <>
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-foreground">{f.title}</span>
          <span className="block text-xs text-muted-foreground">{f.how.replace(/\.$/, '')}</span>
        </span>
        {f.shortcut && <Keys keys={f.shortcut} />}
        {canOpen && <ArrowRight className="w-3.5 h-3.5 shrink-0 text-muted-foreground/60 group-hover:text-primary transition-colors" aria-hidden />}
      </>
    );
    const cls = 'group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left';
    return canOpen
      ? <button key={f.id} type="button" onClick={() => tryIt(f)} title={f.benefit} className={`${cls} hover:bg-muted/70 transition-colors`}>{body}</button>
      : <div key={f.id} title={f.benefit} className={cls}>{body}</div>;
  };

  const section = (label: string, items: HelpFeature[]) => items.length > 0 && (
    <section key={label}>
      <h3 className="px-3 text-xs font-medium text-muted-foreground">{label}</h3>
      <div className="mt-1">{items.map(renderFeature)}</div>
    </section>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="px-5 pt-5 pb-3 space-y-3 text-left">
          <SheetTitle className="font-display font-normal text-2xl">Help</SheetTitle>
          <SheetDescription className="sr-only">Find a feature and where it lives in OneCheckUp.</SheetDescription>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search help" className="pl-8 h-9" />
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-2 pb-6 pt-1 space-y-5">
          {q ? (
            matches.length
              ? section(matches.length === 1 ? '1 result' : `${matches.length} results`, matches)
              : <p className="text-sm text-muted-foreground text-center py-8">Nothing matches “{query}”.</p>
          ) : (
            <>
              {whatsNewFirst && <WhatsNew isNew />}
              {section('On this page', here)}
              {HELP_CATEGORIES.map(c => section(c, rest.filter(f => f.category === c)))}
              <section>
                <h3 className="px-3 text-xs font-medium text-muted-foreground">Shortcuts</h3>
                <div className="mt-1">
                  {EXTRA_SHORTCUTS.map(s => (
                    <div key={s.label} className="flex items-center justify-between gap-3 px-3 py-2 text-sm text-foreground">
                      {s.label}
                      <Keys keys={s.keys} />
                    </div>
                  ))}
                </div>
              </section>
              {!whatsNewFirst && <WhatsNew isNew={false} />}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
